import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';

import { getProcessingStage, transcriptorModel } from '@/entities/transcriptor';

import {
  isLiveProtocolDocument,
  type LiveDictationState,
  type LiveSession,
} from '@/features/audioRecording';

import { EncounterProtocolDocument } from './EncounterProtocolDocument';
import { LiveConsultationDocuments } from './LiveConsultationDocuments';
import { LiveDictationNotice } from './LiveDictationNotice';
import styles from './LiveSessionNote.module.scss';
import { ProcessingStages } from './ProcessingStages';

type Props = {
  session: LiveSession;
  className?: string;
};

// The status line already says the recording is listening; the live notice
// is shown only when live mode is not working as expected.
const isLiveDegraded = ({
  status,
  reason,
  providerError,
}: LiveDictationState) =>
  providerError ||
  status === 'reconnecting' ||
  status === 'interrupted' ||
  (status === 'unavailable' && reason !== 'off');

// The recording's note in the global recording widget: the protocol filling
// in live, editable while recording; a consultation is generated after Stop,
// and while it records its summary's and evaluation's fields are shown.
// After Stop the same note follows the processing to the final document.
export const LiveSessionNote: FC<Props> = ({ session, className }) => {
  const { meta, live } = session;
  const encounter = useAppSelector((state) =>
    transcriptorModel.selectors.selectConsultationById(state, meta.encounterId),
  );
  const recordingThis = useAppSelector(
    ({ transcriptor: { recording } }) =>
      recording.consultationId === meta.encounterId &&
      (recording.isRecording || recording.isPaused),
  );
  const protocols = (encounter?.documents ?? []).filter(isLiveProtocolDocument);

  const waiting = (
    <p className={cn('p3', styles.status)}>
      <FormattedMessage
        id="recordingToast.notePreparing"
        defaultMessage="Preparing the note…"
      />
    </p>
  );

  return (
    <div
      className={cn(styles.note, className)}
      data-component="recording-live-note"
    >
      {encounter && !recordingThis && (
        <ProcessingStages stage={getProcessingStage(encounter)} />
      )}
      {live && recordingThis && isLiveDegraded(live) && (
        <LiveDictationNotice
          status={live.status}
          reason={live.reason}
          providerError={live.providerError}
        />
      )}
      {meta.kind === 'consultation'
        ? recordingThis && (
            <>
              <p className={cn('p3', styles.status)}>
                <FormattedMessage
                  id="recordingToast.consultationNote"
                  defaultMessage="The documents of this consultation are generated after you stop the recording."
                />
              </p>
              {encounter && <LiveConsultationDocuments encounter={encounter} />}
            </>
          )
        : encounter && protocols.length
          ? protocols.map((document) => (
              <EncounterProtocolDocument
                key={document.id}
                encounter={encounter}
                document={document}
                canEdit={meta.canEdit}
                fallback={waiting}
              />
            ))
          : waiting}
    </div>
  );
};
